import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight, Shield } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { PRIVACY_CONTENT } from "./privacyPolicyContent";
import BackToTop from "../components/BackToTop";
import "../styles/privacy-policy.css";

// Renders one content block. Copy lives in privacyPolicyContent.js.
const Block = ({ block }) => {
  if (block.type === 'ul') {
    return (
      <ul className="privacy-list">
        {block.items.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    );
  }
  if (block.type === 'b') {
    return (
      <p className="privacy-p">
        <strong className="privacy-lead">{block.label}</strong> {block.text}
      </p>
    );
  }
  return <p className="privacy-p">{block.text}</p>;
};

const Section = ({ section, open, onToggle, collapsible }) => (
  <section id={section.id} className="privacy-section" data-testid={`privacy-section-${section.id}`}>
    <button
      type="button"
      className="privacy-section__head"
      onClick={collapsible ? onToggle : undefined}
      aria-expanded={open}
      aria-controls={`${section.id}-body`}
      data-testid={`privacy-section-toggle-${section.id}`}
      style={{ cursor: collapsible ? 'pointer' : 'default' }}
    >
      <span className="privacy-section__n">{section.n}.</span>
      <h2 className="privacy-section__title">{section.title}</h2>
      {collapsible && (
        open
          ? <ChevronDown size={18} className="privacy-section__chev" />
          : <ChevronRight size={18} className="privacy-section__chev" />
      )}
    </button>
    {open && (
      <div id={`${section.id}-body`} className="privacy-section__body" data-testid={`privacy-body-${section.id}`}>
        {section.body.map((block, i) => <Block key={i} block={block} />)}
      </div>
    )}
  </section>
);

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => (localStorage.getItem('arvexis_lang') === 'es' ? 'es' : 'en'));
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  const [tocOpen, setTocOpen] = useState(false);
  const [openIds, setOpenIds] = useState(() => new Set());
  const [active, setActive] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const onLang = () => setLang(localStorage.getItem('arvexis_lang') === 'es' ? 'es' : 'en');
    window.addEventListener('storage', onLang);
    const mq = window.matchMedia('(min-width: 1024px)');
    const onSize = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onSize);
    return () => {
      window.removeEventListener('storage', onLang);
      mq.removeEventListener('change', onSize);
    };
  }, []);

  const t = PRIVACY_CONTENT[lang];

  // Desktop reads as a continuous document; the compact viewport reads section by section,
  // opening on the first one so the page is never a wall of headings.
  useEffect(() => {
    setOpenIds(isDesktop
      ? new Set(t.sections.map((s) => s.id))
      : new Set([t.sections[0].id]));
  }, [isDesktop, t.sections]);

  const toggle = (id) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const goTo = (id) => {
    setActive(id);
    setOpenIds((prev) => new Set(prev).add(id));
    setTocOpen(false);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div className="privacy-page" data-testid="privacy-policy-page">
      <header className="privacy-topbar">
        <button
          onClick={() => navigate(-1)}
          className="privacy-back"
          aria-label={lang === 'es' ? 'Volver' : 'Back'}
          data-testid="privacy-back-btn"
        >
          <ArrowLeft size={18} />
        </button>
        <span className="privacy-wordmark">ARVEXIS</span>
        <div style={{ width: 38 }} />
      </header>

      <div className="privacy-shell">
        <nav className="privacy-toc" aria-label={t.tocLabel} data-testid="privacy-toc-desktop">
          <p className="privacy-toc__label">{t.tocLabel}</p>
          <ol className="privacy-toc__list">
            {t.sections.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => goTo(s.id)}
                  className={`privacy-toc__link${active === s.id ? ' is-active' : ''}`}
                  data-testid={`privacy-toc-link-${s.id}`}
                >
                  <span className="privacy-toc__n">{s.n}.</span>
                  {s.title}
                </button>
              </li>
            ))}
          </ol>
          <div className="privacy-toc__foot">
            <Shield size={14} />
            <span>
              {PRIVACY_CONTENT.en.footerLine}
              <br />
              <em>{PRIVACY_CONTENT.es.footerLine}</em>
            </span>
          </div>
        </nav>

        <main className="privacy-main" ref={bodyRef}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <p className="privacy-eyebrow">{t.eyebrow}</p>
            <h1 className="privacy-title" data-testid="privacy-title">{t.title}</h1>
            <p className="privacy-lede">{t.lede}</p>
            <p className="privacy-draft" data-testid="privacy-draft-note">{t.draftNote}</p>
            <p className="privacy-updated" data-testid="privacy-last-updated">{t.lastUpdated}</p>

            <button
              type="button"
              className="privacy-toc-mobile"
              onClick={() => setTocOpen((v) => !v)}
              aria-expanded={tocOpen}
              data-testid="privacy-toc-mobile-toggle"
            >
              <span>{t.tocMobileLabel}</span>
              <ChevronDown size={18} style={{ transform: tocOpen ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
            </button>
            {tocOpen && (
              <ol className="privacy-toc-mobile__list" data-testid="privacy-toc-mobile-list">
                {t.sections.map((s) => (
                  <li key={s.id}>
                    <button type="button" onClick={() => goTo(s.id)} data-testid={`privacy-toc-mobile-link-${s.id}`}>
                      <span className="privacy-toc__n">{s.n}.</span>{s.title}
                    </button>
                  </li>
                ))}
              </ol>
            )}

            <div className="privacy-intro">
              {t.intro.map((line, i) => <p key={i} className="privacy-p">{line}</p>)}
            </div>

            {t.sections.map((s) => (
              <Section
                key={s.id}
                section={s}
                open={openIds.has(s.id)}
                onToggle={() => toggle(s.id)}
                collapsible={!isDesktop}
              />
            ))}

            <footer className="privacy-foot" data-testid="privacy-footer">
              <span className="privacy-wordmark">ARVEXIS</span>
              <p>{t.footerLine}</p>
            </footer>
          </motion.div>
        </main>
      </div>
      <BackToTop lang={lang} />
    </div>
  );
};

export default PrivacyPolicyPage;
